package com.arena_web.data.api

import com.arena_web.data.dto.*
import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.apiDelete
import com.arena_web.data.http.apiGet
import com.arena_web.data.http.apiPost
import com.arena_web.data.http.apiPostMultipart
import com.arena_web.data.http.apiPut
import io.ktor.client.*

class ClubAdminApi(private val client: HttpClient) {

    // ── Club CRUD ──
    suspend fun createClub(request: CreateClubRequest): ApiResponse<ClubDto> {
        return client.apiPost("v1/clubs", request)
    }

    suspend fun uploadClubImage(
        fileBytes: ByteArray,
        fileName: String,
        contentType: String
    ): ApiResponse<ImageUploadResponse> {
        return client.apiPostMultipart("v1/clubs/upload-image", fileBytes, fileName, contentType)
    }

    suspend fun getMyClubs(): ApiResponse<List<ClubDto>> {
        return client.apiGet("v1/clubs/me")
    }

    suspend fun getClub(clubId: String): ApiResponse<ClubDto> {
        return client.apiGet("v1/clubs/$clubId")
    }

    suspend fun updateClub(clubId: String, request: UpdateClubRequest): ApiResponse<ClubDto> {
        return client.apiPut("v1/clubs/$clubId", request)
    }

    suspend fun updateGallery(clubId: String, request: UpdateClubGalleryRequest): ApiResponse<List<ClubGalleryImageDto>> {
        return client.apiPut("v1/clubs/$clubId/gallery", request)
    }

    suspend fun updateRules(clubId: String, request: UpdateClubRulesRequest): ApiResponse<List<ClubRuleDto>> {
        return client.apiPut("v1/clubs/$clubId/rules", request)
    }

    suspend fun deleteClub(clubId: String): ApiResponse<String> {
        return client.apiDelete("v1/clubs/$clubId")
    }

    // ── Fields ──
    suspend fun getFields(clubId: String): ApiResponse<List<FieldDto>> {
        return client.apiGet("v1/clubs/$clubId/fields")
    }

    suspend fun createField(clubId: String, request: CreateFieldRequest): ApiResponse<FieldDto> {
        return client.apiPost("v1/clubs/$clubId/fields", request)
    }

    suspend fun updateField(clubId: String, fieldId: String, request: UpdateFieldRequest): ApiResponse<FieldDto> {
        return client.apiPut("v1/clubs/$clubId/fields/$fieldId", request)
    }

    suspend fun deleteField(clubId: String, fieldId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/fields/$fieldId")
    }

    // ── Schedule ──
    suspend fun getSchedule(clubId: String): ApiResponse<ClubScheduleDto> {
        return client.apiGet("v1/clubs/$clubId/schedule")
    }

    suspend fun updateWorkingHours(clubId: String, request: UpdateWorkingHoursRequest): ApiResponse<Unit> {
        return client.apiPut("v1/clubs/$clubId/schedule/working-hours", request)
    }

    suspend fun createScheduleException(
        clubId: String,
        request: CreateScheduleExceptionRequest
    ): ApiResponse<ScheduleExceptionDto> {
        return client.apiPost("v1/clubs/$clubId/schedule/exceptions", request)
    }

    suspend fun updateScheduleException(
        clubId: String,
        exceptionId: String,
        request: UpdateScheduleExceptionRequest
    ): ApiResponse<ScheduleExceptionDto> {
        return client.apiPut("v1/clubs/$clubId/schedule/exceptions/$exceptionId", request)
    }

    suspend fun deleteScheduleException(clubId: String, exceptionId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/schedule/exceptions/$exceptionId")
    }

    // ── Field Pricing ──
    suspend fun getFieldPricing(clubId: String, fieldId: String): ApiResponse<List<FieldPricingDto>> {
        return client.apiGet("v1/clubs/$clubId/fields/$fieldId/pricing")
    }

    suspend fun updateFieldPricing(
        clubId: String,
        fieldId: String,
        request: UpdateFieldPricingRequest
    ): ApiResponse<Unit> {
        return client.apiPut("v1/clubs/$clubId/fields/$fieldId/pricing", request)
    }

    // ── Services ──
    suspend fun getServices(clubId: String): ApiResponse<List<ClubServiceDto>> {
        return client.apiGet("v1/clubs/$clubId/services")
    }

    suspend fun createService(clubId: String, request: CreateClubServiceRequest): ApiResponse<ClubServiceDto> {
        return client.apiPost("v1/clubs/$clubId/services", request)
    }

    suspend fun updateService(
        clubId: String,
        serviceId: String,
        request: UpdateClubServiceRequest
    ): ApiResponse<ClubServiceDto> {
        return client.apiPut("v1/clubs/$clubId/services/$serviceId", request)
    }

    suspend fun deleteService(clubId: String, serviceId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/services/$serviceId")
    }

    // ── Amenities ──
    suspend fun getAmenities(clubId: String): ApiResponse<List<String>> {
        return client.apiGet("v1/clubs/$clubId/amenities")
    }

    suspend fun updateAmenities(clubId: String, request: UpdateClubAmenitiesRequest): ApiResponse<List<String>> {
        return client.apiPut("v1/clubs/$clubId/amenities", request)
    }

    // ── Dashboard ──
    suspend fun getDashboardStats(
        clubId: String,
        params: Map<String, String> = emptyMap()
    ): ApiResponse<DashboardStatsDto> {
        return client.apiGet("v1/clubs/$clubId/dashboard/stats", params)
    }

    suspend fun getDashboardChart(
        clubId: String,
        params: Map<String, String> = emptyMap()
    ): ApiResponse<DashboardChartDto> {
        return client.apiGet("v1/clubs/$clubId/dashboard/chart", params)
    }

    // ── Bookings (Admin) ──
    suspend fun getBookingCalendar(
        clubId: String,
        params: Map<String, String> = emptyMap()
    ): ApiResponse<BookingCalendarDto> {
        return client.apiGet("v1/clubs/$clubId/dashboard/calendar", params)
    }

    suspend fun getPendingBookings(clubId: String): ApiResponse<List<BookingDto>> {
        return client.apiGet("v1/clubs/$clubId/dashboard/pending-bookings")
    }

    suspend fun adminCreateBooking(clubId: String, request: AdminCreateBookingRequest): ApiResponse<BookingDto> {
        return client.apiPost("v1/clubs/$clubId/bookings", request)
    }

    suspend fun adminEditBooking(
        clubId: String,
        bookingId: String,
        request: EditBookingRequest
    ): ApiResponse<BookingDto> {
        return client.apiPut("v1/clubs/$clubId/bookings/$bookingId/edit", request)
    }

    suspend fun approveBooking(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/approve", Unit)
    }

    suspend fun rejectBooking(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/reject", Unit)
    }

    suspend fun markBookingPaid(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/mark-paid", Unit)
    }

    suspend fun markBookingUnpaid(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/mark-unpaid", Unit)
    }

    suspend fun cancelBooking(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/cancel", Unit)
    }

    suspend fun startBooking(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/start", Unit)
    }

    suspend fun completeBooking(clubId: String, bookingId: String): ApiResponse<BookingDto> {
        return client.apiPut<BookingDto, Unit>("v1/clubs/$clubId/bookings/$bookingId/complete", Unit)
    }

    // ── Clients ──
    suspend fun getClients(
        clubId: String,
        params: Map<String, String> = emptyMap()
    ): ApiResponse<ClubClientsPageDto> {
        return client.apiGet("v1/clubs/$clubId/clients", params)
    }

    suspend fun getClient(clubId: String, clientId: String): ApiResponse<ClubClientCardDto> {
        return client.apiGet("v1/clubs/$clubId/clients/$clientId")
    }

    suspend fun getClientBookings(clubId: String, clientId: String): ApiResponse<BookingHistoryPageDto> {
        return client.apiGet("v1/clubs/$clubId/clients/$clientId/bookings")
    }

    suspend fun getClientDebts(clubId: String, clientId: String): ApiResponse<List<BookingDto>> {
        return client.apiGet("v1/clubs/$clubId/clients/$clientId/debts")
    }

    suspend fun addClientNote(
        clubId: String,
        clientId: String,
        request: AddClientNoteRequest
    ): ApiResponse<ClubClientNoteDto> {
        return client.apiPost("v1/clubs/$clubId/clients/$clientId/notes", request)
    }

    suspend fun deleteClientNote(clubId: String, clientId: String, noteId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/clients/$clientId/notes/$noteId")
    }

    suspend fun blockClient(clubId: String, clientId: String, request: BlockClientRequest): ApiResponse<Unit> {
        return client.apiPost("v1/clubs/$clubId/clients/$clientId/block", request)
    }

    suspend fun unblockClient(clubId: String, clientId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/clients/$clientId/block")
    }

    // ── Club Feed ──
    suspend fun getFeed(clubId: String, params: Map<String, String> = emptyMap()): ApiResponse<ClubPostPageDto> {
        return client.apiGet("v1/public/clubs/$clubId/feed", params)
    }

    suspend fun createPost(clubId: String, request: CreateClubPostRequest): ApiResponse<ClubPostDto> {
        return client.apiPost("v1/clubs/$clubId/feed", request)
    }

    suspend fun deletePost(clubId: String, postId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/clubs/$clubId/feed/$postId")
    }

    suspend fun likePost(clubId: String, postId: String): ApiResponse<Unit> {
        return client.apiPost("v1/clubs/$clubId/feed/$postId/like")
    }

    // ── Club Reviews ──
    suspend fun createReview(clubId: String, request: CreateClubReviewRequest): ApiResponse<ClubReviewDto> {
        return client.apiPost("v1/public/clubs/$clubId/reviews", request)
    }
}
